import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type RegionType } from '@/api/common';
import { useRegions } from '@/api/settings';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  type ComboboxProps,
} from '@/components/Form/pickers';

type Props = ComboboxProps<RegionType, true> & {
  disabled?: boolean;
  className?: string;
  value?: RegionType[];
};

const RegionMultiSelect = ({
  value,
  size,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  ...rest
}: Props) => {
  const { regions, isLoading } = useRegions();
  const regionsById = useMemo(
    () => new Map((regions ?? []).map((region) => [region.id, region])),
    [regions],
  );
  const resolvedValue = useMemo(() => {
    if (!value?.length) return [];
    return value
      .map((region) => regionsById.get(region.id) ?? region)
      .filter((region) => Boolean(region.name));
  }, [regionsById, value]);
  return (
    <Combobox
      data-testid="region-multiselect"
      {...rest}
      multiple
      items={isLoading ? [] : (regions ?? [])}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {resolvedValue.map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder={isLoading ? t`Loading Regions...` : t`Select Regions...`}
        />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxEmpty>
          <Trans>No regions were found...</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: RegionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default RegionMultiSelect;
